import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFinderApi } from "./useFinderApi";
import { formatDistanceToNow, formatRelative } from "date-fns";
import { useAuth0 } from "@auth0/auth0-react";
import { FaChevronRight, FaSearch } from "react-icons/fa";
import { Drawer } from "vaul";
import { useUser } from "./UserContext";

const NeedList = () => {
  const { user } = useUser();
  const { getOpenNeeds, getReservedNeeds, createNeed } = useFinderApi();
  const navigate = useNavigate();

  const [openNeeds, setOpenNeeds] = useState([]);
  const [reservedNeeds, setReservedNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newNeedReferenceIdentifier, setNewNeedReferenceIdentifier] =
    useState("");
  const [isCreatingNeed, setIsCreatingNeed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { logout } = useAuth0();

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    const getNeeds = async () => {
      try {
        const [openNeedsResponse, reservedNeedsResponse] = await Promise.all([
          getOpenNeeds(),
          getReservedNeeds(),
        ]);
        const sortedOpenNeeds = openNeedsResponse
          ? openNeedsResponse
              .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
              .slice(0, 20)
          : [];
        const sortedReservedNeeds = reservedNeedsResponse
          ? reservedNeedsResponse.sort(
              (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
            )
          : [];
        setOpenNeeds(sortedOpenNeeds);
        setReservedNeeds(sortedReservedNeeds);
      } catch (error) {
        console.error("Error fetching needs:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    getNeeds();
  }, [getOpenNeeds, getReservedNeeds]);

  const handleReservationClick = (needId) => {
    navigate(`/needs/${needId}/reservation`);
  };

  const handleOpenNeedClick = (needId) => {
    navigate(`/needs/${needId}/update`);
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  };

  const handleOpenNewNeedDrawer = () => {
    setNewNeedReferenceIdentifier("");
    setDrawerOpen(true);
  };

  const handleReferenceIdentifierChange = (e) => {
    setNewNeedReferenceIdentifier(e.target.value);
  };

  const handleCreateNewNeed = () => {
    setIsCreatingNeed(true);
    const newNeed = {
      reference_identifier: newNeedReferenceIdentifier,
      current_region: null,
      people: [
        {
          age: null,
          gender: null,
          relationship: "self",
        },
      ],
      num_pets: null,
      behavioral_history: null,
    };
    createNeed(newNeed)
      .then((data) => {
        setIsCreatingNeed(false);
        navigate(`/needs/${data.id}/update`, { state: { isNewNeed: true } });
      })
      .catch((error) => {
        setIsCreatingNeed(false);
        console.error("Error creating need:", error);
      });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="flex flex-col h-screen">
      {/* Sticky Header */}
      <header className="sticky top-0 z-30 bg-slate-100 shadow-md border-b border-gray-200  w-full">
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ minHeight: "56px" }}
        >
          <img
            src="/logo-shelterready.png"
            alt="Shelter Ready Logo"
            className="h-9 w-auto"
          />
          <div
            className="flex items-center gap-4 text-sm text-sr-blue relative"
            ref={menuRef}
          >
            <span className="hidden md:block">Welcome, {user?.name}</span>

            <div
              className="w-9 h-9 rounded-full bg-slate-300 flex items-center justify-center text-white text-lg font-bold cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Open menu"
            >
              {user?.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>
            {menuOpen && (
              <div className="absolute right-0 top-10 bg-white border rounded shadow-lg z-10 min-w-[120px]">
                <button
                  onClick={() => {
                    navigate("/settings");
                    setMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 border-b"
                >
                  My Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-grow overflow-auto flex justify-center">
        <div className="w-full max-w-xl flex flex-col justify-between px-4 py-6">
          <main className="flex-1">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-sr-darkblue mb-4">
                Open Reservations
              </h2>
              {reservedNeeds.length > 0 ? (
                reservedNeeds.map((need) => (
                  <button
                    key={need.id}
                    className="flex justify-between items-center w-full text-left bg-white p-4 rounded-xl shadow-md border border-gray-300 mb-4 hover:bg-slate-50 focus:outline-none"
                    onClick={() => handleReservationClick(need.id)}
                  >
                    <div>
                      <p className="text-gray-900 mb-2 font-medium">
                        {need.reference_identifier}
                      </p>
                      <p className="text-gray-600">
                        Shelter: {need?.reservation?.shelter?.name}
                      </p>
                      <p className="text-gray-600 mb-2">
                        Space: {need?.reservation?.space_type?.name}
                      </p>
                      <p className="text-sm text-green-900">
                        Reserved{" "}
                        {formatRelative(
                          new Date(need?.reservation?.created_at),
                          new Date()
                        )}
                      </p>
                    </div>
                    <FaChevronRight className="text-gray-400" />
                  </button>
                ))
              ) : (
                <p className="text-gray-400">There are no open reservations</p>
              )}
            </section>
            <section>
              <h2 className="text-xl font-bold text-sr-darkblue mb-4">
                Recent Shelter Searches
              </h2>
              {openNeeds.length > 0 ? (
                <ul className="space-y-3">
                  {openNeeds.map((need) => (
                    <li key={need.id}>
                      <button
                        className="flex justify-between items-center w-full text-left text-gray-600 border-b border-gray-200 rounded-md p-2 hover:bg-slate-50 focus:outline-none"
                        onClick={() => handleOpenNeedClick(need.id)}
                      >
                        <div>
                          <span className="block font-medium text-gray-700">
                            {need.reference_identifier}
                          </span>
                          <div className="text-sm text-blue-900">
                            {formatDistanceToNow(new Date(need.updated_at), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                        <FaChevronRight className="text-gray-400" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400">There are no recent searches</p>
              )}
            </section>
          </main>
          <footer className="text-center text-sm text-gray-500 py-4 pb-32">
            <p>&copy; 2025 Caravan Studios, a division of TechSoup</p>
            <p>Version 1.3.0</p>
          </footer>
          <div className="h-24"></div>
          <button
            onClick={handleOpenNewNeedDrawer}
            className="fixed bottom-8 right-8 px-6 py-3 bg-sr-blue text-white rounded-full shadow-lg hover:bg-sr-lightbrown flex items-center justify-center text-lg transition duration-300"
          >
            <FaSearch className="mr-2" />
            Start a new search
          </button>
        </div>
      </div>

      {/* Drawer */}
      <Drawer.Root
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        shouldScaleBackground
      >
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40 z-50" />
          <Drawer.Content className="bg-white flex flex-col fixed bottom-0 left-0 right-0 max-h-[96%] rounded-t-[10px] z-50">
            <div className="max-w-md w-full mx-auto flex flex-col overflow-auto p-4 rounded-t-[10px]">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-zinc-300 mb-8" />
              <Drawer.Title className="text-xl font-bold mb-4">
                Search for shelter space
              </Drawer.Title>
              <p className="text-lg text-gray-700 mb-4">
                Let's label this search so you can find it later:
              </p>
              <div className="mb-6">
                <input
                  type="text"
                  id="reference-identifier"
                  className="w-full p-2 border border-gray-300 rounded"
                  defaultValue={newNeedReferenceIdentifier}
                  onChange={handleReferenceIdentifierChange}
                />
                <label
                  className="block text-sm font-medium text-gray-700 mt-2"
                  htmlFor="lookup-descriptor"
                >
                  This Reference Identifier is for your reference only
                </label>
              </div>
              <div className="flex justify-end space-x-4">
                <Drawer.Close>
                  <button className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
                    Cancel
                  </button>
                </Drawer.Close>
                <button
                  className={`px-4 py-2 text-white rounded ${
                    newNeedReferenceIdentifier &&
                    newNeedReferenceIdentifier.trim().length > 0
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-300 cursor-not-allowed"
                  } `}
                  onClick={handleCreateNewNeed}
                  disabled={isCreatingNeed}
                >
                  {isCreatingNeed
                    ? "Creating shelter need..."
                    : "Start searching"}
                </button>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
};

export default NeedList;
